import { Alert, Button, Form, Input, Space, Steps, Typography } from "antd";
import type { FormProps } from "antd";
import { CheckCircle, Loader2, Copy, Check, AlertCircle, RefreshCw, ArrowLeft } from "lucide-react";
import { Fragment, useState, useEffect } from "react";
import React from "react";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { useCurrentProject } from "../../contexts/Project";
import { getCloudService, getProjectService } from "../../backend";
import { CertificateResponse } from "@microtica/ms-aws-sdk";
import { trackResourceAssignDomainFailed, trackResourceDomainCheckSucceeded, trackResourceDomainEntered } from "../../backend/tracking/resource";
import MessageCard from "../cards/MessageCard";
import { notification } from "antd";

const { Text } = Typography;
type RequiredMark = FormProps['requiredMark'];

// wait max 5 minutes for completion of DNS propagation check
const PROPAGATION_WAITING_TIME = 300; // seconds
// wait max 3 minutes for retrieving CNAME property of the certificate
const CERTIFICATE_WAITING_TIME = 180; // seconds

export interface DomainWizardResult {
    domain: string;
    certificateId: string;
    certArn: string;
}

interface DomainWizardProps {
    envId: string;
    resourceName: string;
    domainCNAME: string;
    awsAccount: {
        id: string;
        region: string;
    };
    initialDomain?: string;
    onComplete: (result: DomainWizardResult) => void;
    onCancel: () => void;
    submitButtonText?: string;
    onFooterChange?: (footer: React.ReactNode) => void;
}

const DomainWizard = ({
    envId,
    resourceName,
    domainCNAME,
    awsAccount,
    initialDomain,
    onComplete,
    onCancel,
    submitButtonText = "Complete Setup",
    onFooterChange
}: DomainWizardProps) => {
    const currentProject = useRecoilValue(currentProjectState);
    const { updateCurrentProject } = useCurrentProject();
    const [form] = Form.useForm();
    const [requiredMark] = useState<RequiredMark>('optional');

    const [domain, setDomain] = useState<string | undefined>(initialDomain?.replace('https://', '').replace('http://', ''));
    const [certificateId, setCertificateId] = useState<string>();
    const [CNAMERecord, setCNAMERecord] = useState<{ name: string; value: string }>();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [waitingTime, setWaitingTime] = useState(0);
    const formDomainValue = Form.useWatch('domain', form);

    const shouldInitiateStarterPlanTrial = currentProject?.paymentPlan?.id === "FREE";

    const handleCreatedCertificate = (response: CertificateResponse) => {
        setWaitingTime(0);
        const { id, CNAME } = response;
        setCertificateId(id);
        if (!!CNAME?.name && !!CNAME?.value) {
            setCurrentStep(1);
            setCNAMERecord({ name: CNAME.name, value: CNAME.value });
            setLoading(false);
        } else {
            const interval = setInterval(async () => {
                const { data: { CNAME } } = await getCloudService().fetchCertificate(awsAccount.id, awsAccount.region, id, currentProject!.id)
                    .catch(_ => ({ data: { CNAME: undefined } }));

                setWaitingTime(prev => {
                    const waitingTimeElapsed = prev + 5 > CERTIFICATE_WAITING_TIME;
                    const foundCertificateCname = !!CNAME?.name && !!CNAME?.value;
                    if (foundCertificateCname || waitingTimeElapsed) {
                        clearInterval(interval);
                        setLoading(false);
                        if (foundCertificateCname) {
                            setCurrentStep(1);
                            setCNAMERecord({ name: CNAME!.name!, value: CNAME!.value! });
                            return 0;
                        } else {
                            trackResourceAssignDomainFailed({ envId, resourceName, domain: domain! });
                            return prev + 5;
                        }
                    }
                    return prev + 5;
                });
            }, 5000);
        }
    };

    const requestCertificate = async () => {
        setLoading(true);
        setWaitingTime(0);
        try {
            if (shouldInitiateStarterPlanTrial) {
                const { data: { paymentPlans } } = await getProjectService().getAllPaymentPlans();
                const starterPlan = paymentPlans.find(plan => plan.id === "STARTER")!;
                await getProjectService().changeProjectSubscription(currentProject!.id, {
                    paymentPlanId: starterPlan.id!,
                    priceId: starterPlan.pricing.find(p => p.isDefault)!.id
                });
                await updateCurrentProject(currentProject.id);
            }

            const { data } = await getCloudService().createACMCertificate(awsAccount.id, awsAccount.region, currentProject!.id, {
                domainName: domain!
            });

            trackResourceDomainEntered({ envId, resourceName, domain: domain! });
            handleCreatedCertificate(data);
        } catch (error) {
            setLoading(false);
            notification.error({
                message: "Error requesting certificate",
                description: (error as any)?.response?.data?.message || "An unexpected error occurred."
            });
            trackResourceAssignDomainFailed({ envId, resourceName, domain: domain! });
        }
    };

    const verifyCertificate = async () => {
        setLoading(true);
        setWaitingTime(0);

        const interval = setInterval(async () => {
            const { data: { status } } = await getCloudService().fetchCertificate(awsAccount.id, awsAccount.region, certificateId!, currentProject!.id)
                .catch(_ => ({ data: { status: "UNKNOWN" } }));

            setWaitingTime(prev => {
                const waitingTimeElapsed = prev + 5 > PROPAGATION_WAITING_TIME;
                if (status === "ISSUED" || waitingTimeElapsed) {
                    clearInterval(interval);
                    setLoading(false);
                    if (status === "ISSUED") {
                        setCurrentStep(2);
                        trackResourceDomainCheckSucceeded({ envId, resourceName, domain: domain! });
                        return 0;
                    } else {
                        trackResourceAssignDomainFailed({ envId, resourceName, domain: domain! });
                        return prev + 5;
                    }
                }
                return prev + 5;
            });
        }, 5000);
    };

    const handleComplete = () => {
        const certArn = `arn:aws:acm:${awsAccount.region}:${awsAccount.id}:certificate/${certificateId}`;
        onComplete({ domain: domain!, certificateId: certificateId!, certArn });
    };

    // Extract root domain from subdomain
    const getRootDomain = (domainName?: string): string => {
        if (!domainName) return '';
        const parts = domainName.split('.');
        // For most cases, root domain is the last 2 parts (e.g., example.com)
        // For country code TLDs, it might be 3 parts (e.g., example.co.uk)
        // Common country code TLDs that need 3 parts
        const countryCodeTLDs = ['co.uk', 'com.au', 'co.za', 'com.br', 'co.jp', 'com.mx'];
        const lastTwo = parts.slice(-2).join('.');
        const lastThree = parts.slice(-3).join('.');
        
        // Check if it's a known country code TLD
        if (parts.length >= 3 && countryCodeTLDs.some(tld => domainName.endsWith('.' + tld) || domainName.endsWith(tld))) {
            return lastThree;
        }
        return lastTwo;
    };

    // Helper function to create pipeline-style status tags (matching domain settings)
    const createStatusTag = (
        icon: React.ReactElement,
        text: string,
        color: string,
        size: "small" | "large" = "small"
    ) => {
        const fontSize = size === "large" ? "10px" : "9px";
        const padding = size === "large" ? "3px 8px" : "2px 6px";
        const iconSize = size === "large" ? "11px" : "10px";
        const textColor = color === "#059669" ? "#047857" : color; // Darker green for success states

        return (
            <div style={{
                fontSize: fontSize,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: textColor,
                flexShrink: 0,
                padding: padding,
                borderRadius: "3px",
                backgroundColor: `${color}10`,
                border: `1px solid ${color}20`,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
            }}>
                <span style={{
                    color: color,
                    display: "inline-flex",
                    alignItems: "center"
                }}>
                    {React.cloneElement(icon, { size: iconSize })}
                </span>
                {text}
            </div>
        );
    };

    const steps = [
        {
            title: "Domain",
            content: (
                <Space direction="vertical" size={20} className="full-width">
                    {waitingTime > CERTIFICATE_WAITING_TIME && (
                        <Alert
                            type="warning"
                            message="Certificate request is taking longer than usual. Please try again or contact support."
                            style={{ marginBottom: 0 }}
                        />
                    )}
                    <div style={{ 
                        backgroundColor: '#fff', 
                        padding: '20px', 
                        borderRadius: 6,
                        border: '1px solid #e8e8e8'
                    }}>
                        <Form form={form} layout="vertical" requiredMark={requiredMark}>
                            <Form.Item
                                name="domain"
                                label={<Text strong style={{ fontSize: 14 }}>Custom Domain</Text>}
                                rules={[
                                    { required: true, message: "Please enter a domain" },
                                    {
                                        pattern: /^(((?!-)[A-Za-z0-9-]{1,62})\.){2,}((?!-)[A-Za-z0-9-]{2,62})$/,
                                        message: "Please enter a valid domain (e.g., app.example.com)"
                                    }
                                ]}
                                initialValue={domain}
                                style={{ marginBottom: shouldInitiateStarterPlanTrial && !loading ? 16 : 0 }}
                            >
                                <Input
                                    placeholder="app.example.com"
                                    onChange={(e) => setDomain(e.target.value)}
                                    disabled={loading}
                                    size="large"
                                />
                            </Form.Item>
                        </Form>
                        {shouldInitiateStarterPlanTrial && !loading && (
                            <Alert
                                type="info"
                                message={
                                    <Text style={{ fontSize: 12 }}>
                                        Continuing will initiate a 7-day trial of the{" "}
                                        <a href="https://microtica.com/pricing" target="_blank" rel="noreferrer">Starter plan</a>.
                                    </Text>
                                }
                                style={{ marginTop: 16, marginBottom: 0 }}
                            />
                        )}
                    </div>
                </Space>
            )
        },
        {
            title: "DNS Records",
            content: (
                <Space direction="vertical" size={20} className="full-width">
                    {waitingTime > PROPAGATION_WAITING_TIME && (
                        <Alert
                            type="warning"
                            message={
                                <span>
                                    Propagation check is taking longer than usual.
                                    <br /><br />
                                    Please verify that you've added the CNAME records correctly.
                                </span>
                            }
                            style={{ marginBottom: 0 }}
                        />
                    )}

                    {/* Main Card Panel */}
                    <div style={{ 
                        backgroundColor: '#fff', 
                        padding: '24px', 
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8',
                        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)'
                    }}>
                        {/* Status Bar */}
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            marginBottom: '20px'
                        }}>
                            {createStatusTag(<AlertCircle size={10} />, "Pending Verification", "#f59e0b")}
                        </div>

                        {/* Instructional Text */}
                        <div style={{ marginBottom: '24px' }}>
                            <Text style={{ fontSize: '14px', color: '#595959', lineHeight: '20px' }}>
                                Please set the following CNAME records on{' '}
                                <Text strong style={{ color: 'var(--link-color, #097BB5)', fontSize: '14px' }}>{getRootDomain(domain)}</Text>
                            </Text>
                        </div>

                        {/* DNS Records Table */}
                        <div style={{ marginBottom: '24px' }}>
                            {/* Headers */}
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '100px 1fr 1fr',
                                gap: '32px',
                                marginBottom: '16px',
                                paddingBottom: '12px'
                            }}>
                                <Text style={{ fontSize: '12px', color: '#8c8c8c', fontWeight: 500 }}>Type</Text>
                                <Text style={{ fontSize: '12px', color: '#8c8c8c', fontWeight: 500 }}>Name</Text>
                                <Text style={{ fontSize: '12px', color: '#8c8c8c', fontWeight: 500 }}>Value</Text>
                            </div>

                            {/* First Record - Verification */}
                            {CNAMERecord?.name && (
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: '100px 1fr 1fr',
                                    gap: '32px',
                                    marginBottom: '12px'
                                }}>
                                    <Text strong style={{ fontSize: '13px', color: '#262626' }}>CNAME</Text>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Text strong style={{ fontSize: '13px', color: '#262626', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                                            {CNAMERecord.name}
                                        </Text>
                                        <Text 
                                            copyable={{ 
                                                text: CNAMERecord.name, 
                                                icon: [<Copy size={14} key="copy" style={{ color: '#8c8c8c' }} />, <Check size={14} key="copied" />],
                                                tooltips: ['Copy', 'Copied']
                                            }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Text strong style={{ fontSize: '13px', color: '#262626', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                                            {CNAMERecord.value}
                                        </Text>
                                        <Text 
                                            copyable={{ 
                                                text: CNAMERecord.value, 
                                                icon: [<Copy size={14} key="copy" style={{ color: '#8c8c8c' }} />, <Check size={14} key="copied" />],
                                                tooltips: ['Copy', 'Copied']
                                            }}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Second Record - Domain Pointing */}
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: '100px 1fr 1fr',
                                gap: '32px'
                            }}>
                                <Text strong style={{ fontSize: '13px', color: '#262626' }}>CNAME</Text>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Text strong style={{ fontSize: '13px', color: '#262626', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                                        {domain}
                                    </Text>
                                    <Text 
                                        copyable={{ 
                                            text: domain || '', 
                                            icon: [<Copy size={14} key="copy" style={{ color: '#8c8c8c' }} />, <Check size={14} key="copied" />],
                                            tooltips: ['Copy', 'Copied']
                                        }}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {domainCNAME.includes("execute-api") ? (
                                        <Alert
                                            message="Available after deployment"
                                            type="info"
                                            style={{ padding: '4px 8px', margin: 0, width: '100%' }}
                                        />
                                    ) : (
                                        <>
                                            <Text strong style={{ fontSize: '13px', color: '#262626', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                                                {domainCNAME}
                                            </Text>
                                            <Text 
                                                copyable={{ 
                                                    text: domainCNAME, 
                                                    icon: [<Copy size={14} key="copy" style={{ color: '#8c8c8c' }} />, <Check size={14} key="copied" />],
                                                    tooltips: ['Copy', 'Copied']
                                                }}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Explanatory Text */}
                        <div style={{ 
                            paddingTop: '20px',
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <Text style={{ fontSize: '13px', color: '#595959', lineHeight: '20px' }}>
                                To verify domain ownership and complete the setup, please add the following CNAME records to your domain's DNS configuration. 
                                These records are essential for SSL certificate validation and do not affect your current email or website services.
                            </Text>
                        </div>
                    </div>
                </Space>
            )
        },
        {
            title: "Complete",
            content: (
                <Space direction="vertical" size={24} className="full-width" style={{ textAlign: 'center' }}>
                    <div style={{ 
                        backgroundColor: '#fff', 
                        padding: '32px 20px', 
                        borderRadius: 6,
                        border: '1px solid #e8e8e8'
                    }}>
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(82, 196, 26, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px'
                            }}
                        >
                            <CheckCircle size={32} color="#52c41a" />
                        </div>
                        <div>
                            <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 8 }}>Certificate Verified</Text>
                            <Text type="secondary" style={{ fontSize: 14 }}>
                                Your SSL certificate for <Text strong>{domain}</Text> is ready.
                            </Text>
                        </div>
                    </div>
                </Space>
            )
        }
    ];

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            setLoading(false);
            setWaitingTime(0);
        }
    };

    // Render footer based on current step
    const renderFooter = () => {
        if (currentStep === 0) {
            return (
                <>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button
                        type="primary"
                        onClick={requestCertificate}
                        loading={loading}
                        disabled={!domain || !!form.getFieldError("domain").length}
                    >
                        {loading ? "Requesting..." : "Request Certificate"}
                    </Button>
                </>
            );
        } else if (currentStep === 1) {
            return (
                <>
                    <Button icon={<ArrowLeft size={16} />} onClick={handleBack}>
                        Back
                    </Button>
                    <Button type="primary" onClick={verifyCertificate} loading={loading}>
                        {loading ? "Verifying..." : "Verify DNS"}
                    </Button>
                </>
            );
        } else {
            return (
                <>
                    <Button icon={<ArrowLeft size={16} />} onClick={handleBack}>
                        Back
                    </Button>
                    <Button type="primary" onClick={handleComplete}>
                        {submitButtonText}
                    </Button>
                </>
            );
        }
    };

    // Update footer whenever step, loading state, or form validation changes
    useEffect(() => {
        if (onFooterChange) {
            onFooterChange(renderFooter());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, loading, domain, formDomainValue]);

    return (
        <div style={{ paddingTop: 8 }}>
            <Steps
                current={currentStep}
                size="small"
                items={steps.map((s, i) => ({
                    title: s.title,
                    status: currentStep > i ? 'finish' : currentStep === i ? 'process' : 'wait',
                    icon: loading && currentStep === i ? <Loader2 size={16} className="animate-spin" /> : undefined
                }))}
                style={{ marginBottom: 32, marginTop: 8 }}
            />
            <div
                style={{
                    backgroundColor: 'transparent',
                    borderRadius: 8,
                    padding: 0,
                    minHeight: 350
                }}
            >
                {steps[currentStep].content}
            </div>
        </div>
    );
};

export default DomainWizard;
